import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { useAuth } from '../lib/auth';
import { useProfile } from '../state/profile';
import {
  acknowledgeWelcomeBack,
  checkNickname,
  saveProfile,
} from '../lib/profile';
import { loadCantons, loadNations, localizedName, type Canton, type Nation } from '../lib/static';
import { BottleCap } from '../components/app/BottleCap';
import { CantonShield } from '../components/app/CantonShield';
import type { ApiError } from '../lib/http';

const NICK_RE = /^[\p{L}0-9._-]{3,32}$/u;

/**
 * Minimal first-login onboarding. Two modes off the profile route:
 *  - welcomeBack: returning user re-accepts terms (POST /me/onboarding/light)
 *  - full: terms → nickname + nation cap → canton (PUT /api/profile)
 * On completion we refresh the cached profile and land on /play.
 */
export function Onboarding() {
  const { t, locale, localePath } = useI18n();
  const { accessToken, hydrate } = useAuth();
  const { route, profile, refresh } = useProfile();

  const tokenRef = useRef<string | null>(accessToken);
  tokenRef.current = accessToken;
  const auth = useMemo(() => ({ getToken: () => tokenRef.current, hydrate }), [hydrate]);

  // Route guards: bounce out when there's nothing to onboard.
  useEffect(() => {
    if (route === 'ready') {
      const ret = new URL(location.href).searchParams.get('return');
      location.replace(ret && ret.startsWith('/') && !ret.startsWith('//') ? ret : localePath('/play'));
    } else if (route === 'signed-out') {
      location.replace(localePath('/login'));
    }
  }, [route]);

  if (route === 'welcomeBack') return <WelcomeBack />;
  if (route === 'full') return <FullOnboarding />;
  return (
    <div class="bcapp">
      <div class="bc-spinner">{t('app.loading')}</div>
    </div>
  );

  function WelcomeBack() {
    const [busy, setBusy] = useState(false);
    const [agree, setAgree] = useState(false);
    const [err, setErr] = useState(false);
    async function finish() {
      if (!agree || busy) return;
      setBusy(true);
      setErr(false);
      try {
        await acknowledgeWelcomeBack(auth);
        await refresh();
      } catch {
        setErr(true);
        setBusy(false);
      }
    }
    return (
      <Frame step={1} total={1} title={t('onb.welcome.title')} subtitle={t('onb.welcome.subtitle')}>
        <TermsRow agree={agree} setAgree={setAgree} />
        {err && <p class="onb-err">{t('onb.error')}</p>}
        <button class="bc-cta" disabled={!agree || busy} onClick={finish}>
          {busy ? t('onb.saving') : t('onb.welcome.cta')}
        </button>
      </Frame>
    );
  }

  function FullOnboarding() {
    const [step, setStep] = useState(0);
    const [agree, setAgree] = useState(false);
    const [nickname, setNickname] = useState(profile?.nickname ?? '');
    const [cap, setCap] = useState<string | undefined>(profile?.avatarCap);
    const [canton, setCanton] = useState<string | undefined>(profile?.canton);
    const [nations, setNations] = useState<Nation[]>([]);
    const [cantons, setCantons] = useState<Canton[]>([]);
    const [nickState, setNickState] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'format'>('idle');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
      loadNations().then(setNations).catch(() => {});
      loadCantons().then(setCantons).catch(() => {});
    }, []);

    // Debounced nickname availability probe.
    useEffect(() => {
      const n = nickname.trim();
      if (!n) return setNickState('idle');
      if (!NICK_RE.test(n)) return setNickState('format');
      setNickState('checking');
      const id = setTimeout(async () => {
        try {
          const res = await checkNickname(auth, n);
          setNickState(res.available ? 'ok' : res.reason === 'format' ? 'format' : 'taken');
        } catch {
          setNickState('idle');
        }
      }, 350);
      return () => clearTimeout(id);
    }, [nickname]);

    async function submit() {
      if (busy) return;
      setBusy(true);
      setErr(null);
      try {
        await saveProfile(auth, { nickname: nickname.trim(), avatarCap: cap, canton });
        await refresh();
        // route flips to 'ready' → the effect above redirects to /play.
      } catch (e) {
        const ae = e as ApiError;
        if (ae.code === 'nickname_taken') {
          setNickState('taken');
          setStep(1);
        } else {
          setErr(t('onb.error'));
        }
        setBusy(false);
      }
    }

    const canStep1 = nickState === 'ok' && !!cap;

    return (
      <>
        {step === 0 && (
          <Frame step={1} total={3} title={t('onb.terms.title')} subtitle={t('onb.terms.subtitle')}>
            <TermsRow agree={agree} setAgree={setAgree} />
            <button class="bc-cta" disabled={!agree} onClick={() => setStep(1)}>
              {t('onb.continue')}
            </button>
          </Frame>
        )}

        {step === 1 && (
          <Frame step={2} total={3} title={t('onb.identity.title')} subtitle={t('onb.identity.subtitle')}>
            <label class="onb-field">
              <span>{t('onb.identity.nickname_label')}</span>
              <input
                class="bc-input"
                value={nickname}
                maxLength={32}
                placeholder={t('onb.identity.nickname_ph')}
                onInput={(e) => setNickname((e.target as HTMLInputElement).value)}
              />
              <small class={`onb-hint onb-hint--${nickState}`}>
                {nickState === 'checking' && t('onb.identity.checking')}
                {nickState === 'ok' && t('onb.identity.available')}
                {nickState === 'taken' && t('onb.identity.taken')}
                {nickState === 'format' && t('onb.identity.format')}
              </small>
            </label>

            <div class="onb-field">
              <span>{t('onb.identity.cap_label')}</span>
              <div class="onb-capgrid">
                {nations.map((n) => (
                  <button
                    key={n.code}
                    type="button"
                    class={`onb-capcell${cap === n.code ? ' is-active' : ''}`}
                    title={localizedName(n.name, locale)}
                    onClick={() => setCap(n.code)}
                  >
                    <BottleCap flag={n.flag} code={n.code} size={40} />
                  </button>
                ))}
              </div>
            </div>

            <div class="onb-actions">
              <button class="bc-pill" onClick={() => setStep(0)}>{t('onb.back')}</button>
              <button class="bc-cta" disabled={!canStep1} onClick={() => setStep(2)}>
                {t('onb.continue')}
              </button>
            </div>
          </Frame>
        )}

        {step === 2 && (
          <Frame step={3} total={3} title={t('onb.canton.title')} subtitle={t('onb.canton.subtitle')}>
            <div class="onb-cantongrid">
              {cantons.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  class={`onb-cantoncell${canton === c.code ? ' is-active' : ''}`}
                  onClick={() => setCanton(c.code)}
                >
                  <CantonShield code={c.code} size={22} />
                  <span>{localizedName(c.name, locale)}</span>
                </button>
              ))}
            </div>
            {err && <p class="onb-err">{err}</p>}
            <div class="onb-actions">
              <button class="bc-pill" onClick={() => setStep(1)}>{t('onb.back')}</button>
              <button class="bc-cta" disabled={!canton || busy} onClick={submit}>
                {busy ? t('onb.saving') : t('onb.finish')}
              </button>
            </div>
          </Frame>
        )}
      </>
    );
  }

  function TermsRow({ agree, setAgree }: { agree: boolean; setAgree: (v: boolean) => void }) {
    return (
      <label class="onb-terms">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree((e.target as HTMLInputElement).checked)} />
        <span>
          {t('onb.terms.accept_a')}{' '}
          <a href={localePath('/terms')} target="_blank">{t('onb.terms.terms_link')}</a>
          {' '}{t('onb.terms.accept_b')}{' '}
          <a href={localePath('/privacy')} target="_blank">{t('onb.terms.privacy_link')}</a>.
        </span>
      </label>
    );
  }

  function Frame({
    step,
    total,
    title,
    subtitle,
    children,
  }: {
    step: number;
    total: number;
    title: string;
    subtitle: string;
    children: ComponentChildren;
  }) {
    return (
      <div class="bcapp">
        <div class="onb-wrap">
          <div class="onb-card">
            <div class="onb-progress">
              {Array.from({ length: total }, (_, i) => (
                <span key={i} class={i < step ? 'is-on' : ''} />
              ))}
            </div>
            <h1 class="onb-title bc-serif">{title}</h1>
            <p class="onb-sub">{subtitle}</p>
            {children}
          </div>
        </div>
      </div>
    );
  }
}
